import { useState } from "react";
import { ModeSelect } from "../../components/Dialogs.tsx";
import { EffortSelect, ModelPicker } from "../../components/Pickers.tsx";
import { ErrorBox, Field, Loading, useAction } from "../../components/ui.tsx";
import { api, useApi } from "../../lib/api.ts";
import { ago } from "../../lib/format.ts";
import { useSettings } from "../../lib/settings.ts";
import type { ClaudeStatus, SystemStatus } from "../../lib/types.ts";

export function ClaudeLoginCard({ claude, onRecheck, checking }: { claude: ClaudeStatus; onRecheck: () => void; checking: boolean }) {
  const tone = claude.loggedIn ? "ok" : claude.installed ? "warn" : "bad";
  return (
    <div className={`callout ${tone}`}>
      <div className="row between">
        <div className="stack tight">
          <div className="row">
            <i className={`dot ${tone}`} />
            <b>{!claude.installed ? "Claude Code is not installed" : claude.loggedIn ? "Logged in" : "Not logged in"}</b>
            {claude.version && <span className="muted">· {claude.version}</span>}
            {claude.authMethod && claude.authMethod !== "none" && <span className="muted">· {claude.authMethod}</span>}
          </div>
          <div className="muted small">Checked {ago(claude.checkedAt)} with `claude auth status` on the server.</div>
        </div>
        <button className="btn sm" disabled={checking} onClick={onRecheck}>
          {checking ? "Checking…" : "Check again"}
        </button>
      </div>
      {!claude.loggedIn && (
        <div className="stack tight" style={{ marginTop: 10 }}>
          {!claude.installed ? (
            <span>
              Install it on the server: <code>npm install -g @anthropic-ai/claude-code</code>
            </span>
          ) : (
            <span>
              Log in on the server, as the user the service runs as: <code>claude login</code> (choose your Max account). Sessions use that login, not an API key.
            </span>
          )}
        </div>
      )}
      {claude.detail && (
        <details style={{ marginTop: 8 }}>
          <summary className="muted small">Details</summary>
          <pre className="small" style={{ marginTop: 6 }}>
            {claude.detail}
          </pre>
        </details>
      )}
    </div>
  );
}

export function useClaudeCheck() {
  const system = useApi<SystemStatus>("/api/system");
  const [recheck, checking] = useAction(async () => system.mutate(await api<SystemStatus>("/api/system?refresh=1"), { revalidate: false }));
  return { system, recheck, checking };
}

export function ClaudeSettings() {
  const { system, recheck, checking } = useClaudeCheck();
  const settings = useSettings();
  const [model, setModel] = useState<string | null>(null);
  const [effort, setEffort] = useState<string | null>(null);
  const [max, setMax] = useState<number | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [save, saving] = useAction(async () => {
    await settings.save({
      ...(model !== null ? { "claude.default_model": model.trim() || null } : {}),
      ...(effort !== null ? { "claude.default_effort": effort || null } : {}),
      ...(max !== null ? { "claude.max_concurrent": max } : {}),
      ...(mode !== null ? { "claude.default_permission_mode": mode } : {}),
    });
    setModel(null);
    setEffort(null);
    setMax(null);
    setMode(null);
  }, "Saved");

  if (!settings.data) return settings.error ? <ErrorBox error={settings.error} /> : <Loading />;
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Claude</h1>
          <div className="sub">The login sessions run under, and the defaults new sessions start with.</div>
        </div>
      </div>
      {system.data ? <ClaudeLoginCard claude={system.data.claude} onRecheck={() => recheck()} checking={checking} /> : system.error ? <ErrorBox error={system.error} /> : <Loading />}
      <section className="card">
        <h2>Defaults</h2>
        <div className="grid3">
          <Field label="Default model" hint="Used when neither the session nor the project sets one; new projects start with it.">
            <ModelPicker value={model ?? settings.get("claude.default_model", "")} onChange={setModel} />
          </Field>
          <Field label="Default effort" hint="How hard Claude thinks. Models without effort support ignore it.">
            <EffortSelect value={effort ?? settings.get("claude.default_effort", "")} onChange={setEffort} />
          </Field>
          <Field label="Max concurrent sessions" hint="Above this, new sessions wait in Queued. Protects your usage window.">
            <input type="number" min={1} max={32} value={max ?? settings.get("claude.max_concurrent", 4)} onChange={(e) => setMax(Number(e.target.value) || 1)} />
          </Field>
          <Field label="Default permission mode" hint="For new projects.">
            <ModeSelect value={mode ?? settings.get("claude.default_permission_mode", "acceptEdits")} onChange={setMode} />
          </Field>
        </div>
        <div>
          <button className="btn primary" disabled={saving || (model === null && effort === null && max === null && mode === null)} onClick={() => save()}>
            Save
          </button>
        </div>
      </section>
    </>
  );
}
